// ────────────────────────────────────────────────────────────────────────────
// JellyfinClient — thin fetch wrapper for the three Jellyfin playback
// reporting endpoints.  Intentionally minimal; no retry, no queue.
//
// Invariants:
//   - Every call throws JellyfinHttpError on non-2xx.  Callers (ProgressReporter)
//     decide what to do — start() surfaces the throw so the state machine goes
//     to ERROR; progress polls swallow + log so a single bad tick doesn't
//     tear down the session.
//   - 8s AbortController timeout per request.  Matches the 10s poll cadence:
//     a hung socket must fail within one interval so the next tick can proceed.
//   - Token sent as X-Emby-Token header (not query param).  Query-param form
//     is only used for subtitle URLs that are fetched externally, not here.
//
// Not handled here: media/subtitle file fetches — those go through the
// browser's fetch (monitored by FetchLogger) with their own timeouts.
// ────────────────────────────────────────────────────────────────────────────

interface PlaybackStartParams {
  itemId: string
  mediaSourceId: string
  playSessionId: string
  audioStreamIndex: number
  subtitleStreamIndex: number | null
  positionTicks: number
  canSeek: boolean
  /** Jellyfin PlayMethod ("DirectStream"/"Transcode"). Omitting it makes the
   * server default to Transcode, mislabeling remux sessions on the dashboard. */
  playMethod: string | null
}

interface PlaybackProgressParams {
  itemId: string
  mediaSourceId: string
  playSessionId: string
  audioStreamIndex: number
  subtitleStreamIndex: number | null
  positionTicks: number
  isPaused: boolean
  isMuted: boolean
  volumeLevel: number
  /** See PlaybackStartParams.playMethod — progress reports overwrite the
   * session's PlayState, so it must be sent here too. */
  playMethod: string | null
}

interface PlaybackStoppedParams {
  itemId: string
  mediaSourceId: string
  playSessionId: string
  positionTicks: number
}

export class JellyfinHttpError extends Error {
  constructor(public readonly status: number, public readonly path: string) {
    super(`[JellyfinClient] ${path} → ${status}`)
    this.name = "JellyfinHttpError"
  }
}

// Progress reporting POSTs must not stall the event loop behind a dead socket.
// 8s is aggressive on purpose: these calls are tiny, and if the Jellyfin host
// is unreachable we want to surface failure inside one polling interval (10s).
// Media/subtitle fetches are NOT routed through this class.
const REPORT_TIMEOUT_MS = 8_000

export class JellyfinClient {
  constructor(
    private readonly serverUrl: string,
    private readonly token: string,
  ) {}

  async reportPlaybackStart(params: PlaybackStartParams): Promise<void> {
    await this.post("/Sessions/Playing", {
      ItemId: params.itemId,
      MediaSourceId: params.mediaSourceId,
      PlaySessionId: params.playSessionId,
      AudioStreamIndex: params.audioStreamIndex,
      SubtitleStreamIndex: params.subtitleStreamIndex ?? undefined,
      PositionTicks: params.positionTicks,
      CanSeek: params.canSeek,
      PlayMethod: params.playMethod ?? undefined,
    })
  }

  async reportPlaybackProgress(params: PlaybackProgressParams): Promise<void> {
    await this.post("/Sessions/Playing/Progress", {
      ItemId: params.itemId,
      MediaSourceId: params.mediaSourceId,
      PlaySessionId: params.playSessionId,
      AudioStreamIndex: params.audioStreamIndex,
      SubtitleStreamIndex: params.subtitleStreamIndex ?? undefined,
      PositionTicks: params.positionTicks,
      IsPaused: params.isPaused,
      IsMuted: params.isMuted,
      VolumeLevel: params.volumeLevel,
      PlayMethod: params.playMethod ?? undefined,
    })
  }

  async reportPlaybackStopped(params: PlaybackStoppedParams): Promise<void> {
    await this.post("/Sessions/Playing/Stopped", {
      ItemId: params.itemId,
      MediaSourceId: params.mediaSourceId,
      PlaySessionId: params.playSessionId,
      PositionTicks: params.positionTicks,
    })
  }

  private async post(path: string, body: unknown): Promise<void> {
    const url = `${this.serverUrl}${path}`
    console.log(`[JellyfinClient] [POST] ${url} ${JSON.stringify(body)}`)

    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), REPORT_TIMEOUT_MS)
    let res: Response
    try {
      res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Emby-Token": this.token,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      })
    } finally {
      clearTimeout(timer)
    }
    if (!res.ok) throw new JellyfinHttpError(res.status, path)
  }
}
